import { Component, computed, DestroyRef, effect, inject, input, OnDestroy, OnInit, Signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, tap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { FormMultiSelectComponent } from '@shared/forms';
import { AvatarComponent, UnavailableComponent } from '@shared/components';
import { InitialsPipe, MediaSrcPipe } from '@shared/pipes';
import { UserFacade } from '@app/store/user';
import { FormOptionsFacade } from '@app/store/form-options';
import { Item } from '@app/store/form-options/models';
import { AssessorAssignmentsFacade } from '@app/store/assessor-assignments';

const LOOKUP_DEBOUNCE_MS = 750;

@Component({
    selector: 'app-assessor-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        AsyncPipe,
        ButtonModule,
        MessageModule,
        FormMultiSelectComponent,
        AvatarComponent,
        InitialsPipe,
        MediaSrcPipe,
        UnavailableComponent,
    ],
})
export class EditComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly userFacade = inject(UserFacade);
    private readonly assessorAssignmentsFacade = inject(AssessorAssignmentsFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    readonly id = input.required<string>();

    readonly user = this.userFacade.user;
    readonly saving = this.assessorAssignmentsFacade.saving;
    readonly error = computed(() => this.userFacade.error() ?? this.assessorAssignmentsFacade.error());

    readonly loading = computed(
        () =>
            this.userFacade.loading() !== false ||
            this.assessorAssignmentsFacade.loading() !== false ||
            this.formOptionsFacade.organisationsLoaded() === null ||
            this.assessorAssignmentsFacade.schoolsLoaded() === null ||
            this.assessorAssignmentsFacade.coursesLoaded() === null ||
            this.assessorAssignmentsFacade.packagesLoaded() === null,
    );

    readonly organisationOptions = this.formOptionsFacade.organisations;
    readonly schoolOptions = this.assessorAssignmentsFacade.schools;
    readonly schoolsLoading = this.assessorAssignmentsFacade.schoolsLoading;
    readonly courseOptions = this.assessorAssignmentsFacade.courses;
    readonly coursesLoading = this.assessorAssignmentsFacade.coursesLoading;
    readonly packageOptions = this.assessorAssignmentsFacade.packages;
    readonly packagesLoading = this.assessorAssignmentsFacade.packagesLoading;

    readonly form = this.formBuilder.group({
        organisationIds: this.formBuilder.control<string[]>([]),
        schoolIds: this.formBuilder.control<string[]>([]),
        courseIds: this.formBuilder.control<string[]>({ value: [], disabled: true }),
        packageIds: this.formBuilder.control<string[]>({ value: [], disabled: true }),
    });

    constructor() {
        effect(() => {
            const assignments = this.assessorAssignmentsFacade.assignments();
            if (!assignments) {
                return;
            }

            this.form.patchValue(assignments, { emitEvent: false });
            this.syncContentControls();
        });

        // Only user edits arrive here: every programmatic write in this component passes emitEvent: false.
        this.form.controls.organisationIds.valueChanges
            .pipe(
                debounceTime(LOOKUP_DEBOUNCE_MS),
                tap((organisationIds) => this.assessorAssignmentsFacade.getSchools(organisationIds)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.form.controls.schoolIds.valueChanges
            .pipe(
                tap(() => this.syncContentControls()),
                debounceTime(LOOKUP_DEBOUNCE_MS),
                tap((schoolIds) => {
                    this.assessorAssignmentsFacade.getCourses(schoolIds);
                    this.assessorAssignmentsFacade.getPackages(schoolIds);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.dropUnavailable(this.form.controls.schoolIds, this.schoolOptions);
        this.dropUnavailable(this.form.controls.courseIds, this.courseOptions);
        this.dropUnavailable(this.form.controls.packageIds, this.packageOptions);

        effect(() => {
            if (this.assessorAssignmentsFacade.saved()) {
                this.close();
            }
        });
    }

    ngOnInit() {
        this.userFacade.getUser(this.id());
        this.assessorAssignmentsFacade.getAssignments(this.id());

        if (this.formOptionsFacade.organisationsLoaded() !== true) {
            this.formOptionsFacade.getOrganisations();
        }
    }

    ngOnDestroy(): void {
        this.userFacade.clear();
        this.assessorAssignmentsFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }

        this.assessorAssignmentsFacade.updateAssignments(this.id(), this.form.getRawValue());
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }

    /**
     * Courses and packages are scoped to schools, so without a school selected there is nothing to pick from.
     * Their values are cleared as well: a disabled control still reaches the payload through getRawValue().
     */
    private syncContentControls(): void {
        const hasSchools = this.form.controls.schoolIds.value.length > 0;
        const contentControls = [this.form.controls.courseIds, this.form.controls.packageIds];

        for (const control of contentControls) {
            if (hasSchools) {
                control.enable({ emitEvent: false });
            } else {
                control.setValue([], { emitEvent: false });
                control.disable({ emitEvent: false });
            }
        }
    }

    /**
     * After the user narrows a parent selection, ids the narrowed list no longer offers are dropped.
     * The form is pristine while the stored assignment loads, so what the backend returned is never touched.
     */
    private dropUnavailable(control: FormControl<string[]>, options: Signal<Item[]>): void {
        effect(() => {
            const available = new Set(options().map((option) => option.value));

            untracked(() => {
                if (this.form.pristine) {
                    return;
                }

                const kept = control.value.filter((id) => available.has(id));
                if (kept.length !== control.value.length) {
                    control.setValue(kept);
                }
            });
        });
    }
}
